import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Seo, Mark, Container , VideoSlot } from '../components/marketing/Primitives';
import { useVideo } from '../cms/ContentProvider';
import {
  specimenQuestion,
  showPricing,
  liveExams,
  inDevelopmentExam,
  comingSoonSkills,
  studyModes,
  alsoIncluded,
  integrityGates,
  integrityCoda,
  honestyMoments,
  faq,
} from '../data/prepData';

/* The interactive specimen: a real production question. Select, submit,
   and the reveal state shows the explanation and the provenance badge,
   exactly as the product does. */
function InteractiveSpecimen() {
  const q = specimenQuestion;
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [sourceNote, setSourceNote] = useState(false);
  const correct = submitted && selected === q.correctKey;

  function optionClasses(key) {
    const base = 'w-full rounded border px-3 py-2.5 text-left text-[13px] leading-snug transition-colors';
    if (!submitted) {
      return `${base} ${
        selected === key ? 'border-seal bg-seal/10 text-bright' : 'border-hairline text-slate hover:border-seal/50 hover:text-bright'
      }`;
    }
    if (key === q.correctKey) return `${base} border-emerald-400/70 bg-emerald-400/10 text-bright`;
    if (key === selected) return `${base} border-rose-400/70 bg-rose-400/10 text-bright`;
    return `${base} border-hairline text-faint`;
  }

  return (
    <div className="specimen-card !rotate-0">
      <div className="flex items-baseline justify-between gap-4">
        <p className="text-[11px] font-semibold tracking-wide text-slate">
          {q.blockLabel} · {q.positionLabel}
        </p>
        <p className="text-[11px] text-faint">
          {q.tag} · {q.cognitiveLevel}
        </p>
      </div>

      <p className="mt-3 text-[13px] leading-relaxed text-bright">{q.stem}</p>

      <div className="mt-4 space-y-2">
        {q.options.map((opt) => (
          <button
            key={opt.key}
            type="button"
            disabled={submitted}
            onClick={() => setSelected(opt.key)}
            className={optionClasses(opt.key)}
            aria-pressed={selected === opt.key}
          >
            <span className="mr-2 font-semibold text-bright">{opt.key}.</span>
            {opt.text}
            {submitted && opt.key === q.correctKey && (
              <span className="ml-2 text-[11px] font-semibold text-seal">key</span>
            )}
          </button>
        ))}
      </div>

      {!submitted ? (
        <button
          type="button"
          onClick={() => setSubmitted(true)}
          disabled={!selected}
          className="btn-primary mt-4 w-full !py-2.5 text-center disabled:cursor-not-allowed disabled:opacity-40"
        >
          Submit
        </button>
      ) : (
        <div className="mt-4">
          <p className={`text-sm font-semibold ${correct ? 'text-seal' : 'text-rose-300'}`}>
            {correct ? 'Correct' : `Incorrect: the answer is ${q.correctKey}`}
          </p>
          <p className="mt-2 max-h-44 overflow-y-auto pr-1 text-[12px] leading-relaxed text-slate">
            {q.explanation} <span className="text-faint">({q.claimRef})</span>
          </p>

          {/* The provenance badge: the product's core trust signal. */}
          <div className="mt-4 rounded border border-hairline-dark bg-ink-deep/80 px-4 py-3">
            <p className="text-[11px] font-semibold text-bright">Source: {q.review.title}</p>
            <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
              <p className="text-[11px] text-slate">Sourced from {q.review.sourcedFrom}</p>
              <button
                type="button"
                onClick={() => setSourceNote((v) => !v)}
                className="text-[11px] font-semibold text-seal underline decoration-seal/40 underline-offset-2 hover:decoration-seal"
              >
                Read the review
              </button>
            </div>
            {sourceNote && (
              <p className="mt-2 border-t border-hairline-dark pt-2 text-[11px] leading-snug text-faint">
                In the product, this opens the certified source review as a full PDF, streamed against your
                subscription, with the exact sentence the answer was drawn from quoted beneath it.
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setSelected(null);
              setSubmitted(false);
              setSourceNote(false);
            }}
            className="mt-3 text-[12px] font-medium text-slate underline decoration-hairline underline-offset-4 hover:text-bright"
          >
            Try it again
          </button>
        </div>
      )}
    </div>
  );
}

/* The readiness card: the honest number, drawn as the product draws it. */
function ReadinessCard() {
  return (
    <div className="rounded-media border border-hairline bg-surface p-6" aria-hidden="true">
      <p className="text-[11px] font-semibold tracking-wide text-slate">Preparation progress</p>
      <div className="mt-4 space-y-4">
        <div>
          <div className="flex items-baseline justify-between">
            <p className="text-[12px] text-faint">Early: too little evidence for a number</p>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-ink-deep">
            <div className="h-2 w-[18%] rounded-full bg-slate/50" />
          </div>
          <p className="mt-1 text-[11px] text-faint">Keep going</p>
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <p className="text-[12px] text-slate">With evidence: an approximate band</p>
            <p className="text-[13px] font-semibold text-bright">~54%</p>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-ink-deep">
            <div className="h-2 w-[54%] rounded-full bg-seal/60" />
          </div>
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <p className="text-[12px] text-slate">Earned: a precise figure</p>
            <p className="text-[13px] font-semibold text-seal">78%</p>
          </div>
          <div className="mt-1.5 h-2 rounded-full bg-ink-deep">
            <div className="h-2 w-[78%] rounded-full bg-seal" />
          </div>
          <p className="mt-1 text-[11px] text-faint">41 of 52 blueprint concepts mastered</p>
        </div>
      </div>
      <p className="mt-5 border-t border-hairline pt-3 text-[11px] leading-snug text-faint">
        Deliberately never called a pass probability.
      </p>
    </div>
  );
}

export default function PrepPage() {
  const [openFaq, setOpenFaq] = useState(null);
  const demoVideo = useVideo('prep.session', null);

  return (
    <>
      <Seo
        title="EdAI Prep"
        description="Prepare against the same validated corpus your exam is built from. Practice questions, clinical cases, mock exams, and an adaptive study plan across USMLE, MCCQE, UKMLA, AMC, and PRES, every item traced to a physician-certified source."
      />

      {/* Hero: the claim on the left, a real question you can answer on the right. */}
      <section className="overflow-hidden bg-ink text-paper-dark-text">
        <Container className="grid items-start gap-14 py-20 md:py-28 lg:grid-cols-[1fr_1.05fr] lg:gap-12">
          <div className="lg:pt-6">
            <Mark dark>EdAI Prep · For candidates</Mark>
            <h1 className="hero-rise mt-4 font-serif text-4xl leading-[1.06] md:text-5xl">
              Prepare against the same validated corpus your exam is built from.
            </h1>
            <p className="hero-rise-2 mt-6 max-w-measure text-lg leading-relaxed text-slate-dark-text">
              Spaced-repetition study and calibrated practice, scoped to what your board or jurisdiction actually
              requires. Every question traces to a physician-certified source you can open mid-question.
            </p>
            <p className="hero-rise-2 mt-4 max-w-measure text-sm leading-relaxed text-faint">
              The question on the right is a real production item. Answer it.
            </p>
            <div className="hero-rise-3 mt-9 flex flex-wrap gap-4">
              <Link to="/contact" className="btn-primary-on-dark">Start preparing</Link>
              <a href="#exams" className="btn-secondary-on-dark">See the exams</a>
            </div>
          </div>
          <div className="hero-rise-3">
            <InteractiveSpecimen />
          </div>
        </Container>
      </section>

      {/* Exams: the catalog, priced per market, honest about what is not live. */}
      <section id="exams" className="py-20 md:py-28">
        <Container>
          <Mark>The exams</Mark>
          <h2 className="mt-3 max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            Five licensing exams live today. Each in its own jurisdiction’s lane.
          </h2>
          <p className="mt-5 max-w-measure-wide leading-relaxed text-slate">
            The architecture works for any jurisdiction with a blueprint: blueprint in, validated preparation out.
            Content is retrieved from a jurisdiction-scoped slice of the corpus, so the right drug names, reference
            ranges, and referral thresholds stay in the right exam.
          </p>

          <div className="mt-12 grid gap-px overflow-hidden rounded-media border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-3">
            {liveExams.map((exam) => (
              <Link
                key={exam.id}
                to={`/products/prep/${exam.id}`}
                className="group flex flex-col bg-surface p-7 transition-colors hover:bg-ink-deep/60"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-serif text-xl text-bright">{exam.name}</h3>
                  {showPricing && <p className="text-sm font-semibold text-seal">{exam.price}</p>}
                </div>
                <p className="mt-1 text-sm text-slate">
                  {exam.detail} · {exam.jurisdiction}
                </p>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">{exam.note}</p>
                <p className="mt-5 text-sm font-semibold text-bright underline decoration-hairline underline-offset-4 transition-colors group-hover:decoration-seal">
                  Prep for {exam.name}
                </p>
              </Link>
            ))}
            <Link to="/products/prep/enarm" className="group flex flex-col bg-surface p-7 opacity-80 transition-colors hover:bg-ink-deep/60 hover:opacity-100">
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="font-serif text-xl text-bright">{inDevelopmentExam.name}</h3>
                <p className="rounded border border-hairline px-2 py-0.5 text-[11px] font-semibold text-slate">
                  In development
                </p>
              </div>
              <p className="mt-1 text-sm text-slate">
                {inDevelopmentExam.detail} · {inDevelopmentExam.jurisdiction}
              </p>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">{inDevelopmentExam.note}</p>
              <p className="mt-5 text-sm font-semibold text-bright underline decoration-hairline underline-offset-4 transition-colors group-hover:decoration-seal">
                About ENARM on Prep
              </p>
            </Link>
          </div>

          <p className="mt-6 text-sm leading-relaxed text-faint">
            Clinical-skills examinations ({comingSoonSkills.join(', ')}) are coming and not yet live. When something is
            not ready here, it says so.
          </p>
        </Container>
      </section>

      {/* The four study modes. */}
      <section className="border-y border-hairline bg-ink-deep/40 py-20 md:py-28">
        <Container>
          <Mark>How you study</Mark>
          <h2 className="mt-3 max-w-[24ch] font-serif text-3xl leading-tight md:text-4xl">
            Four ways in. One corpus underneath.
          </h2>
          <div className="mt-12 grid gap-x-14 gap-y-12 md:grid-cols-2">
            {studyModes.map((mode) => (
              <article key={mode.id} className="border-t border-hairline pt-6">
                <h3 className="font-serif text-xl text-bright">{mode.name}</h3>
                <p className="mt-1 text-sm font-medium text-seal">{mode.lead}</p>
                <p className="mt-3 leading-relaxed text-slate">{mode.text}</p>
                <ul className="mt-4 space-y-2">
                  {mode.details.map((d) => (
                    <li key={d} className="flex gap-2.5 text-sm leading-snug text-slate">
                      <span aria-hidden="true" className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-[1px] bg-seal" />
                      {d}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>

          <div className="mt-14 grid gap-px overflow-hidden rounded-media border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4">
            {alsoIncluded.map((item) => (
              <div key={item.name} className="bg-surface p-6">
                <h3 className="text-sm font-semibold text-bright">{item.name}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-slate">{item.text}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* The integrity pipeline. */}
      <section className="py-20 md:py-28">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
            <div>
              <Mark>Verified before it reaches you</Mark>
              <h2 className="mt-3 max-w-[20ch] font-serif text-3xl leading-tight md:text-4xl">
                Every item passes three gates. Or it never ships.
              </h2>
              <p className="mt-5 leading-relaxed text-slate">
                Generation from a certified source is the start, not the point. An AI system turning a certified claim
                into a five-option question can still introduce errors in the packaging. So every generated item runs a
                stack of automated integrity checks before a learner ever sees it.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-faint">{integrityCoda}</p>
            </div>
            <ol className="space-y-4">
              {integrityGates.map((gate, i) => (
                <li key={gate.name} className="flex gap-5 rounded-media border border-hairline bg-surface p-6">
                  <span
                    aria-hidden="true"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-seal/60 font-serif text-sm text-seal"
                  >
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-semibold text-bright">{gate.name}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate">{gate.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Container>
      </section>

      {/* Honesty as a feature. */}
      <section className="border-y border-hairline bg-ink py-20 text-paper-dark-text md:py-28">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr] lg:gap-16">
            <div>
              <Mark dark>What we will not tell you</Mark>
              <h2 className="mt-3 max-w-[22ch] font-serif text-3xl leading-tight md:text-4xl">
                The honest version of every number.
              </h2>
              <div className="mt-10 grid gap-x-12 gap-y-8 sm:grid-cols-2">
                {honestyMoments.map((m) => (
                  <div key={m.title}>
                    <h3 className="font-semibold text-bright">{m.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-dark-text">{m.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:pt-14">
              <ReadinessCard />
            </div>
          </div>
        </Container>
      </section>


      {/* Demo film, published from the content console at /admin */}
      <section className="border-y border-hairline bg-ink py-20 text-paper-dark-text md:py-24">
        <Container>
          <Mark dark>See it</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Watch a study session</h2>
          <p className="mt-4 max-w-measure leading-relaxed text-slate-dark-text">The adaptive plan, a clinical case with sourced explanations, and the spaced-repetition review flow.</p>
          <div className="mt-10 lg:max-w-measure-wide">
            <VideoSlot video={demoVideo} title="Watch a study session" text="The adaptive plan, a clinical case with sourced explanations, and the spaced-repetition review flow." />
          </div>
        </Container>
      </section>
      {/* FAQ. */}
      <section className="py-20 md:py-28">
        <Container>
          <Mark>Questions candidates ask</Mark>
          <h2 className="mt-3 font-serif text-3xl leading-tight md:text-4xl">Asked and answered.</h2>
          <div className="mt-10 max-w-measure-wide divide-y divide-hairline border-y border-hairline">
            {faq.map((item, i) => (
              <div key={item.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  className="flex w-full items-baseline justify-between gap-6 py-5 text-left"
                >
                  <span className="font-semibold text-bright">{item.q}</span>
                  <span aria-hidden="true" className="text-seal">{openFaq === i ? '\u2212' : '+'}</span>
                </button>
                {openFaq === i && <p className="pb-6 leading-relaxed text-slate">{item.a}</p>}
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Closing CTA. */}
      <section className="border-t border-hairline bg-ink-deep/40 py-20">
        <Container className="text-center">
          <h2 className="mx-auto max-w-[26ch] font-serif text-3xl leading-tight md:text-4xl">
            The source behind every answer. The name behind every source.
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-primary">Start preparing</Link>
            <Link to="/products/suites" className="btn-secondary">Built from the same corpus as EdAI Suites</Link>
          </div>
        </Container>
      </section>
    </>
  );
}
